import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { instance as axios } from '../../config/axiosInstance';
import Pagination from 'react-js-pagination';
import useDebounce from '../../hooks/useDebounce';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import NotFound from '../../components/not-found/NotFound';
import findSerialNumber from '../../utils/findSerialNumber';
import common from '../../routes/common';
import { fetchUtmManagementApi } from '../../services/userManagement';

const { NOT_FOUND } = common;

const UTMManagement = () => {
  const [utmContentFilter, setUtmContentFilter] = useState('');
  const [allUtmContent, setAllUtmContent] = useState([]);
  const [utmContentTotals, setUtmContentTotals] = useState({});

  useEffect(() => {
    async function fetchContentList() {
      try {
        const res = await fetch('https://adminapi.vedicmeet.com/api/admin/utm-management/utm-content-list');
        // const res = await fetch('http://localhost:8000/api/admin/utm-management/utm-content-list');
        const json = await res.json();
        if (json.success) setAllUtmContent(json.data);
      } catch (e) {}
    }
    fetchContentList();
    // Fetch total UTM content stats for dashboard summary
    async function fetchContentTotals() {
      try {
        const res = await fetch('http://localhost:8000/api/admin/utm-management/utm-content-counts');
        const json = await res.json();
        if (json.success) setUtmContentTotals(json.data);
      } catch (e) {}
    }
    fetchContentTotals();
  }, []);

  const [searchParam, setSearchParam] = useSearchParams();
  let activePage = +searchParam.get('page') || 1;
  let pageLimit = +searchParam.get('limit') || 10;
  const [search, setSearch] = useState('');
  const debounceValue = useDebounce(search || '', 300);
  const [total, setTotal] = useState(0);
  const [utmList, setUtmList] = useState([]);
  const [loading, showLoader, hideLoader] = useFullPageLoader();
  // *** Move tabIndex before any usage
  const [tabIndex, setTabIndex] = useState(0);

  const fetchUtmList = async (debounceValue, activeTab) => {
    try {
      showLoader();
      const params = {
        page: String(activePage),
        limit: String(pageLimit),
        search: debounceValue,
      };
      // Add filter for tab
      if (activeTab === 0) {
        params['matched_user_id'] = 'not_null'; // Registered users
      } else if (activeTab === 1) {
        params['matched_user_id'] = 'null';     // Anonymous users
      }
      if (utmContentFilter) {
        params['utm_content'] = utmContentFilter;
      }
      const { data } = await fetchUtmManagementApi(params);
      if (data.success) {
        setUtmList(data.data || []);
        setTotal(data.total || 0);
      } else {
        setUtmList([]);
        setTotal(0);
      }
    } catch (error) {
      setUtmList([]);
      setTotal(0);
    } finally {
      hideLoader();
    }
  };

  // Pagination handler
  const handlePageChange = (event) => {
    searchParam.set('page', event);
    setSearchParam(searchParam);
  };

  // Page limit
  const handlePageLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    searchParam.set('limit', newLimit);
    setSearchParam(searchParam);
  };

  // Search
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    searchParam.delete('page');
    setSearchParam(searchParam);
  };
  // Refresh filter
  const refreshHandler = () => {
    setSearch('');
    searchParam.delete('limit');
    searchParam.delete('page');
    setSearchParam(searchParam);
  };

  useEffect(() => {
    fetchUtmList(debounceValue, tabIndex);
    // eslint-disable-next-line
  }, [searchParam, debounceValue, tabIndex, utmContentFilter]);

  useEffect(() => {
    if (Number(activePage) === 1) {
      searchParam.delete('page');
    }
    setSearchParam(searchParam);
  }, [searchParam]);

  // Table columns
  const columns = [
    // { key: 'source', label: 'Source' },
    { key: 'utm_source', label: 'UTM Source' },
    { key: 'utm_medium', label: 'UTM Medium' },
    { key: 'utm_campaign', label: 'UTM Campaign' },
    { key: 'utm_term', label: 'UTM Term' },
    { key: 'utm_content', label: 'UTM Content' },
    { key: 'matched_user_id', label: 'Matched User ID' },
    { key: 'ip_address', label: 'IP Address' },
    { key: 'user_agent', label: 'User Agent' },
    { key: 'platform', label: 'Platform' },
    { key: 'original_url', label: 'Original URL' },
    { key: 'redirect_url', label: 'Redirect URL' },
    { key: 'clicked_at', label: 'Clicked At' },
    // { key: 'expires_at', label: 'Expires At' },
    { key: 'createdAt', label: 'Created At' },
    // { key: 'is_app_installed', label: 'App Installed' },
  ];

  // Custom style for URL cells
  const twoLineUrl = {
    maxWidth: 180,
    display: 'block',
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.2',
    maxHeight: '2.6em', // accommodate 2 lines
  };

  const thStyle = {
    whiteSpace: 'nowrap',
    padding: '8px 10px',
    fontWeight: 600,
    background: '#faf9f7'
  };
  const tdStyle = {
    padding: '7px 8px',
    verticalAlign: 'middle',
  };

  // Tab: 0 = registered, 1 = anonymous
  // Make sure useState for tabIndex is defined only ONCE and at the top

  // UTM Content Click Count Calculation (Dynamic)
  const utmContentCounts = {};
  let nullContentCount = 0;
  utmList.forEach((rec) => {
    const content = (rec.utm_content || '').trim();
    if (content) {
      utmContentCounts[content] = (utmContentCounts[content] || 0) + 1;
    } else {
      nullContentCount += 1;
    }
  });
  const summaryArr = [
    ...Object.entries(utmContentCounts).map(
      ([content, count]) => `"${content}" se ${count}`
    ),
  ];
  if (nullContentCount > 0) {
    summaryArr.push(`UTM Content null se ${nullContentCount}`);
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
      <div className="TitleBox">
        <h4 className="Title">UTM Management</h4>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={()=>setTabIndex(0)} style={{background:tabIndex===0?'#ff8989':'#fff',color:tabIndex===0?'#fff':'#222',padding:'5px 16px',border:'1px solid #ff8989',borderRadius:4,fontWeight:600,cursor:'pointer'}}>Registered Users</button>
          <button onClick={()=>setTabIndex(1)} style={{background:tabIndex===1?'#ff8989':'#fff',color:tabIndex===1?'#fff':'#222',padding:'5px 16px',border:'1px solid #ff8989',borderRadius:4,fontWeight:600,cursor:'pointer'}}>Anonymous Users</button>
        </div>
        <div className="block_user_button">
            <button className="refresh_button" onClick={refreshHandler} style={{marginLeft:8}}>
              Refresh
            </button>
          </div>
        </div>
        {/* UTM Content Global Summary */}
        <div
          style={{
            margin: '12px 0 16px 0',
            padding: '12px 20px',
            background: '#f5faf8',
            borderRadius: 8,
            fontWeight: 500,
            boxShadow: '0 1px 8px #eaeaea',
            fontSize: 15,
            minHeight: 45,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontWeight: 700, marginRight: 10 }}>
            UTM Content Summary:
          </span>
          {Object.keys(utmContentTotals).length > 0
            ? Object.entries(utmContentTotals)
                .filter(([key]) => key !== "meta_organic\\")
                .map(([key, val], idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-block',
                      background: key === "UTM Content null" ? '#ffeeee' : '#e2f0ff',
                      color: key === "UTM Content null" ? '#d11b2c' : '#282a2c',
                      border: key === "UTM Content null" ? '1px solid #e4b0b0' : '1px solid #b8daf6',
                      borderRadius: 14,
                      padding: '6px 15px',
                      boxShadow: '0 2px 7px #ededed',
                      marginBottom: 5,
                      wordBreak: 'break-all',
                      fontWeight: 600,
                      fontSize: 15,
                      minWidth: 60,
                    }}
                  >
                    {key} ={val}
                  </span>
                ))
            : <span>Loading...</span>
          }
        </div>
        <div className="FilterBox FilterArea" style={{display:"flex",alignItems:"center",gap:12}}>
          <div className="form-group search_lg_size userSearch">
            <input
              type="search"
              className={`form-control ${search && 'active'}`}
              placeholder="Search UTM/source/platform/ip/url..."
              value={search || ''}
              style={{ maxWidth: 320, display: 'inline-block' }}
              onChange={handleSearchChange}
              autoFocus
            />
          </div>
          {/* UTM Content Filter Dropdown */}
          <select
            value={utmContentFilter}
            onChange={e => setUtmContentFilter(e.target.value)}
            style={{minWidth:70,maxWidth:300,padding:'4px 12px',borderRadius:4,height:36,border:'1px solid #eee',marginRight:300}}>
            <option value="">All UTM Content</option>
            {allUtmContent
              .filter(val => val !== "meta_organic\\")
              .map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
          </select>
        </div>
        <div className="content-table" style={{overflowX: 'auto'}}>
          <table>
            <thead>
              <tr>
                <th style={thStyle}>S. No.</th>
                {columns.map((c) => (
                  <th style={thStyle} key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center' }}>Loading...</td></tr>
              ) : utmList.length === 0 ? (
                <NotFound cols={columns.length + 1} msg="No UTM records found!" icon={NOT_FOUND} loader={loading} />
              ) : (
                utmList.map((rec, idx) => (
                  <tr key={rec._id || idx}>
                    <td style={tdStyle}>{findSerialNumber(idx, activePage, pageLimit)}</td>
                    {/* <td style={tdStyle}>{rec.source || ''}</td> */}
                    <td style={tdStyle}>{rec.utm_source || ''}</td>
                    <td style={tdStyle}>{rec.utm_medium || ''}</td>
                    <td style={tdStyle}>{rec.utm_campaign || ''}</td>
                    <td style={tdStyle}>{rec.utm_term || ''}</td>
                    <td style={tdStyle}>{rec.utm_content || ''}</td>
                    <td style={tdStyle}>{rec.matched_user_id || ''}</td>
                    <td style={tdStyle}>{rec.ip_address || ''}</td>
                    <td style={{...tdStyle, maxWidth: 1000, minWidth: 300, wordBreak: 'break-word'}}>{rec.user_agent || ''}</td>
                    <td style={tdStyle}>{rec.platform || ''}</td>
                    <td style={tdStyle}>
                      {rec.original_url ? <a href={rec.original_url} target="_blank" rel="noopener noreferrer" style={twoLineUrl}>{rec.original_url}</a> : ''}
                    </td>
                    <td style={tdStyle}>
                      {rec.redirect_url ? <a href={rec.redirect_url} target="_blank" rel="noopener noreferrer" style={twoLineUrl}>{rec.redirect_url}</a> : ''}
                    </td>
                    <td style={tdStyle}>{rec.clicked_at ? new Date(rec.clicked_at).toLocaleString() : ''}</td>
                    {/* <td style={tdStyle}>{rec.expires_at ? new Date(rec.expires_at).toLocaleString() : ''}</td> */}
                    <td style={tdStyle}>{rec.createdAt ? new Date(rec.createdAt).toLocaleString() : ''}</td>
                    {/* <td style={tdStyle}>{rec.is_app_installed ? 'Yes' : 'No'}</td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Custom Pagination UI */}
        <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center" style={{marginTop: 16}}>
          <div>
            <span style={{marginRight: 8}}>Show: </span>
            <select value={pageLimit} onChange={handlePageLimitChange} style={{padding:'4px 8px', borderRadius:4}}>
              {[5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{marginLeft:8}}> per page</span>
          </div>
          <div>
            {total > pageLimit && (
              <Pagination
                activePage={activePage}
                itemsCountPerPage={pageLimit}
                totalItemsCount={total}
                pageRangeDisplayed={5}
                onChange={handlePageChange}
                innerClass="pagination custom-pagination"
                itemClass="page-item"
                linkClass="page-link"
                prevPageText={<span style={{fontWeight:700}}>&lt; Prev</span>}
                nextPageText={<span style={{fontWeight:700}}>Next &gt;</span>}
                firstPageText={<span style={{fontWeight:700}}>&laquo; First</span>}
                lastPageText={<span style={{fontWeight:700}}>Last &raquo;</span>}
              />
            )}
          </div>
        </div>
        <style>{`
          .custom-pagination {
            display: flex;
            list-style: none;
            padding-left: 0;
            margin-bottom: 0;
          }
          .page-item {margin: 0 4px;}
          .page-link {
            display: block;
            padding: 6px 12px;
            color: #5a5a5a;
            background: #efefef;
            border: 1px solid #e5e8ee;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 600;
            min-width: 36px;
            text-align: center;
            text-decoration: none;
            transition: background 0.15s;
          }
          .page-item.active .page-link {
            background: #ff8989!important;
            color: white;
            border-color: #ff8989;
          }
          .pagination .page-link[aria-disabled="true"] {
            opacity: 0.6;
            pointer-events: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default UTMManagement;
